import React, { useState, useEffect, useRef } from "react";
import { Plus, Trash2, Download, Loader2, Eye, ImagePlus } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const todayISO = () => new Date().toISOString();
const plusDaysISO = (d) => new Date(Date.now() + d * 86400000).toISOString();
const toDateInput = (iso) => { try { return new Date(iso).toISOString().slice(0, 10); } catch { return ""; } };
const newItem = () => ({ dia_mm: "", length_mm: "", size_g: "", qty: "", price: "" });

export default function QuoteForm({ lead, onClose }) {
  const [form, setForm] = useState(null);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileRef = useRef(null);

  const endpoint = lead?.id ? `/leads/${lead.id}/quotations/pdf` : `/quotations/pdf`;

  useEffect(() => {
    const l = lead || {};
    setPreviewUrl(null);
    setForm({
      quote_date: todayISO(), valid_till: plusDaysISO(14),
      buyer_company: l.company_name || "", buyer_name: l.contact_name || "",
      buyer_phone: l.phone || "", buyer_email: l.email || "", buyer_city: l.city || "",
      laminate_type: "", cap_type: "", cap_color: "",
      plate_charges: "Rs.1000/color", freight: "As per actual", lead_time: "15 days", taxes: "18%",
      notes: "",
    });
    setItems([newItem()]);
  }, [lead]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setItem = (i, k, v) => setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const addItem = () => setItems((arr) => [...arr, newItem()]);
  const removeItem = (i) => setItems((arr) => (arr.length > 1 ? arr.filter((_, idx) => idx !== i) : arr));

  const extractFromImage = async (b64) => {
    setExtracting(true);
    try {
      const { data } = await api.post("/quotations/extract", { images: [b64] });
      if (data?.status !== "success") { toast.error(data?.reason || "Couldn't read the snapshot"); return; }
      const f = data.fields || {};
      if (Array.isArray(f.items) && f.items.length) {
        setItems(f.items.map((it) => ({ ...newItem(), ...it })));
      }
      setForm((prev) => ({
        ...prev,
        buyer_company: f.buyer_company || prev.buyer_company,
        buyer_name: f.buyer_name || prev.buyer_name,
        buyer_phone: f.buyer_phone || prev.buyer_phone,
        buyer_email: f.buyer_email || prev.buyer_email,
        buyer_city: f.buyer_city || prev.buyer_city,
        laminate_type: f.laminate_type || prev.laminate_type,
        cap_type: f.cap_type || prev.cap_type,
        cap_color: f.cap_color || prev.cap_color,
        plate_charges: f.plate_charges || prev.plate_charges,
        freight: f.freight || prev.freight,
        lead_time: f.lead_time || prev.lead_time,
        taxes: f.taxes || prev.taxes,
      }));
      toast.success("Snapshot read — values filled in. Please review.");
    } catch (err) {
      toast.error("Couldn't read the snapshot");
    } finally { setExtracting(false); }
  };

  const fileToB64 = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

  const onSnapshot = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const b64 = await fileToB64(file);
    await extractFromImage(b64);
  };

  // Paste (Ctrl+V) an image anywhere in the form to autofill
  useEffect(() => {
    const onPaste = async (e) => {
      if (previewUrl || extracting) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const it of items) {
        if (it.type && it.type.startsWith("image/")) {
          const file = it.getAsFile();
          if (file) {
            e.preventDefault();
            const b64 = await fileToB64(file);
            await extractFromImage(b64);
          }
          return;
        }
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewUrl, extracting]);

  const buildPayload = () => ({ ...form, items });
  const validate = () => {
    if (!items.some((it) => it.dia_mm || it.size_g || it.qty || it.price)) {
      toast.error("Add at least one row"); return false;
    }
    return true;
  };
  const fileName = () => `quotation-${(form.buyer_company || lead?.company_name || "quote").replace(/\s+/g, "-")}.pdf`;
  const saveBlob = (blob) => {
    const url = window.URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = fileName();
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  const download = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      const res = await api.post(`${endpoint}?save=true`, buildPayload(), { responseType: "blob" });
      saveBlob(res.data);
      toast.success("Quotation downloaded");
      onClose?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't generate the quotation"); }
    finally { setBusy(false); }
  };
  const preview = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      const res = await api.post(endpoint, buildPayload(), { responseType: "blob" });
      if (previewUrl) window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })));
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't generate the preview"); }
    finally { setBusy(false); }
  };
  const downloadFromPreview = async () => {
    setBusy(true);
    try {
      const res = await api.post(`${endpoint}?save=true`, buildPayload(), { responseType: "blob" });
      saveBlob(res.data);
      toast.success("Quotation downloaded");
    } catch (e) { toast.error("Couldn't download"); }
    finally { setBusy(false); }
  };
  const backToEdit = () => { if (previewUrl) window.URL.revokeObjectURL(previewUrl); setPreviewUrl(null); };

  if (!form) return null;

  return (
    <div>
      {previewUrl ? (
        <iframe title="quote-preview" src={previewUrl} className="w-full h-[68vh] border border-slate-200 rounded-lg bg-slate-50" data-testid="q-preview-frame" />
      ) : (
        <div className="space-y-5">
          {/* Snapshot autofill */}
          <div className="flex items-center justify-between rounded-lg border border-dashed border-rose-200 bg-rose-50/50 p-3">
            <div className="text-xs text-slate-600">
              <b>Have a rate sheet screenshot?</b> Upload it or just paste it (<kbd className="px-1 py-0.5 rounded border border-rose-300 bg-white text-[10px] font-mono">Ctrl</kbd>+<kbd className="px-1 py-0.5 rounded border border-rose-300 bg-white text-[10px] font-mono">V</kbd>) and AI will fill the table & terms for you.
            </div>
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={extracting} data-testid="q-snapshot-btn" className="gap-1.5 border-rose-300 text-rose-700">
              {extracting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />} Upload snapshot
            </Button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onSnapshot} data-testid="q-snapshot-input" />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><Label className="text-xs">Quote Date</Label><Input type="date" value={toDateInput(form.quote_date)} onChange={(e) => set("quote_date", new Date(e.target.value).toISOString())} data-testid="q-date" /></div>
            <div><Label className="text-xs">Valid Till</Label><Input type="date" value={toDateInput(form.valid_till)} onChange={(e) => set("valid_till", new Date(e.target.value).toISOString())} data-testid="q-valid" /></div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-2">Customer</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">Company</Label><Input value={form.buyer_company} onChange={(e) => set("buyer_company", e.target.value)} data-testid="q-company" /></div>
              <div><Label className="text-xs">Contact name</Label><Input value={form.buyer_name} onChange={(e) => set("buyer_name", e.target.value)} /></div>
              <div><Label className="text-xs">Phone</Label><Input value={form.buyer_phone} onChange={(e) => set("buyer_phone", e.target.value)} /></div>
              <div><Label className="text-xs">City</Label><Input value={form.buyer_city} onChange={(e) => set("buyer_city", e.target.value)} /></div>
            </div>
          </div>

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-slate-700">Line items</h4>
              <Button size="sm" variant="outline" onClick={addItem} data-testid="q-add-item" className="h-8 gap-1"><Plus className="h-3.5 w-3.5" /> Add row</Button>
            </div>
            <div className="space-y-2">
              <div className="hidden sm:grid grid-cols-6 gap-2 text-[11px] font-medium text-slate-500 px-1">
                <span>Dia (mm)</span><span>Length (mm)</span><span>Size (g)</span><span>Qty</span><span>Price</span><span></span>
              </div>
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-6 gap-2 items-center" data-testid={`q-item-${i}`}>
                  <Input value={it.dia_mm} onChange={(e) => setItem(i, "dia_mm", e.target.value)} data-testid={`q-dia-${i}`} />
                  <Input value={it.length_mm} onChange={(e) => setItem(i, "length_mm", e.target.value)} />
                  <Input value={it.size_g} onChange={(e) => setItem(i, "size_g", e.target.value)} />
                  <Input value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} data-testid={`q-qty-${i}`} />
                  <Input value={it.price} onChange={(e) => setItem(i, "price", e.target.value)} data-testid={`q-price-${i}`} />
                  <div>{items.length > 1 && <button onClick={() => removeItem(i)} data-testid={`q-remove-${i}`} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Terms */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Laminate Type</Label><Input value={form.laminate_type} onChange={(e) => set("laminate_type", e.target.value)} placeholder="e.g. 12/250 white" data-testid="q-laminate" /></div>
            <div><Label className="text-xs">Cap Type</Label><Input value={form.cap_type} onChange={(e) => set("cap_type", e.target.value)} placeholder="e.g. Stand up" data-testid="q-captype" /></div>
            <div><Label className="text-xs">Cap Colour</Label><Input value={form.cap_color} onChange={(e) => set("cap_color", e.target.value)} placeholder="e.g. white" data-testid="q-capcolor" /></div>
            <div><Label className="text-xs">Plate Charges</Label><Input value={form.plate_charges} onChange={(e) => set("plate_charges", e.target.value)} /></div>
            <div><Label className="text-xs">Freight</Label><Input value={form.freight} onChange={(e) => set("freight", e.target.value)} /></div>
            <div><Label className="text-xs">Lead Time</Label><Input value={form.lead_time} onChange={(e) => set("lead_time", e.target.value)} /></div>
            <div><Label className="text-xs">Taxes</Label><Input value={form.taxes} onChange={(e) => set("taxes", e.target.value)} /></div>
            <div className="sm:col-span-2"><Label className="text-xs">Notes (optional)</Label><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-4">
        {previewUrl ? (
          <>
            <Button variant="outline" onClick={backToEdit} data-testid="q-back-edit">Back to edit</Button>
            <Button onClick={downloadFromPreview} disabled={busy} data-testid="q-download" className="bg-rose-600 hover:bg-rose-700 gap-1.5">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download PDF
            </Button>
          </>
        ) : (
          <>
            {onClose && <Button variant="outline" onClick={onClose}>Cancel</Button>}
            <Button variant="outline" onClick={preview} disabled={busy} data-testid="q-preview" className="gap-1.5">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Preview
            </Button>
            <Button onClick={download} disabled={busy} data-testid="q-download-direct" className="bg-rose-600 hover:bg-rose-700 gap-1.5">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download PDF
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
